import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { http } from '@/lib/api/client'
import type { AppSettings, Resource } from '@/lib/api/types'

export interface SettingsResponse {
  values: AppSettings
  history: Partial<Record<keyof AppSettings, { updated_at: string | null; updated_by: string | null }>>
  can_edit: boolean
}

export function useSettings() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: async () => (await http.get<Resource<SettingsResponse>>('/settings')).data,
  })
}

export function useSaveSettings<K extends keyof AppSettings>(key: K) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (value: AppSettings[K]) =>
      (await http.put<Resource<SettingsResponse>>(`/settings/${key}`, value)).data,
    onSuccess: (data) => queryClient.setQueryData(['settings'], data),
  })
}

/** Settings → Cloudflare cache: connected or not, who can clear it and the last clear. */
export interface CloudflareCacheStatus {
  configured: boolean
  can_clear: boolean
  max_urls: number
  last: { at: string; by: string | null; everything: boolean; urls: number } | null
}

export function useCloudflareCache() {
  return useQuery({
    queryKey: ['cloudflare-cache'],
    queryFn: async () => (await http.get<Resource<CloudflareCacheStatus>>('/cloudflare-cache')).data,
  })
}

export function useClearCloudflareCache() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { everything: true } | { everything: false; urls: string[] }) =>
      (await http.post<Resource<CloudflareCacheStatus>>('/cloudflare-cache/clear', input)).data,
    onSuccess: (data) => queryClient.setQueryData(['cloudflare-cache'], data),
  })
}
